import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Flame } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      nav(u.role === "admin" ? "/admin" : (loc.state?.from || "/"));
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGZhc2hpb24lMjBlZGl0b3JpYWwlMjBtb2RlbHxlbnwwfHx8fDE3ODE5NTY4MTd8MA&ixlib=rb-4.1.0&q=85" className="absolute inset-0 w-full h-full object-cover" alt=""/>
        <div className="absolute inset-0 hero-overlay"/>
        <div className="absolute bottom-10 left-10 text-white max-w-md">
          <div className="flex items-center gap-2 mb-3"><Flame/><span className="font-display font-bold text-2xl">Stylefire</span></div>
          <h2 className="font-display text-3xl font-bold">Where Style Meets Fire.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold">Welcome back</h1>
          <p className="text-slate-500 text-sm mt-1">Sign in to continue shopping.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email"/>
            <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password"/>
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs text-[#004BCA] font-semibold" data-testid="login-forgot">Forgot password?</Link>
            </div>
            <button data-testid="login-submit" disabled={loading} className="btn-primary-sf w-full py-3 rounded-full font-semibold disabled:opacity-60">{loading?"Signing in…":"Sign in"}</button>
          </form>
          <div className="my-5 flex items-center gap-3"><div className="h-px bg-slate-200 flex-1"/><span className="text-xs text-slate-400">OR</span><div className="h-px bg-slate-200 flex-1"/></div>
          <button data-testid="google-signin" onClick={googleLogin} className="w-full border border-slate-200 rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-slate-50">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4"/> Continue with Google
          </button>
          <p className="text-sm text-slate-600 mt-6 text-center">New to Stylefire? <Link to="/register" data-testid="login-register-link" className="text-[#004BCA] font-semibold">Create account</Link></p>
        </div>
      </div>
    </main>
  );
}

export function Field({ label, type="text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e)=>onChange(e.target.value)} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:border-[#004BCA] focus:bg-white"/>
    </label>
  );
}
